import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { convertMPStoKMPH } from 'app/agriculture/utils';
import { useContext } from 'react';
import { WeatherDashboardContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/WeatherDashboardContextProvider';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';
import { useDailyRainfall } from 'app/agriculture/hooks/useDailyRainfall/useDailyRainfall';

import { DynamicUvIndicator } from 'app/agriculture/components/DynamicIcons/DynamicUvIndicator/DynamicUvIndicator';
import { DynamicTemperatureIndicator } from 'app/agriculture/components/DynamicIcons/DynamicTemperatureIndicator/DynamicTemperatureIndicator';
import { DynamicWindIndicator } from 'app/agriculture/components/DynamicIcons/DynamicWindIndicator/DynamicWindIndicator';
import { DynamicRainIndicator } from 'app/agriculture/components/DynamicIcons/DynamicRainIndicator/DynamicRainIndicator';
import { DynamicHumidityIndicator } from 'app/agriculture/components/DynamicIcons/DynamicHumidityIndicator/DynamicHumidityIndicator';
import { StatWithDirection } from 'app/agriculture/components/StatWithDirection/StatWithDirection';

export function CurrentWeatherStatus() {
    const { weatherStationId } = useContext(WeatherDashboardContext);

    if (weatherStationId == null) {
        throw Error('No weather station selected');
    }
    const now = useThrottledNow();
    const { temperature, uvIndex, humidity, windSpeed, windDirection } =
        useSnapshot({
            snapshotType: 'Weather',
            parentId: weatherStationId,
            cursorTimeOverride: now,
        }) ?? {};

    const rainfall = useDailyRainfall({ deviceId: weatherStationId });

    return (
        <>
            <StatDisplay
                id={'StatDisplay-Current-Temperature'}
                name="Temperature"
                Icon={(props) => (
                    <DynamicTemperatureIndicator
                        {...props}
                        temperature={temperature ?? undefined}
                    />
                )}
                value={temperature}
                unit="°C"
                emptyStateMessage="-"
                decimalPlaces={1}
            />
            <StatDisplay
                id={'StatDisplay-Current-Humidity'}
                Icon={(props) => (
                    <DynamicHumidityIndicator {...props} humidity={humidity ?? undefined} />
                )}
                name="Humidity"
                value={humidity}
                unit="%"
                emptyStateMessage="-"
                decimalPlaces={1}
            />
            <StatDisplay
                id={'StatDisplay-Current-Rainfall'}
                Icon={(props) => <DynamicRainIndicator {...props} rainfall={rainfall} />}
                name="Rainfall (Today)"
                value={rainfall}
                unit="mm"
                emptyStateMessage="-"
                decimalPlaces={1}
            />
            <StatDisplay
                id={'StatDisplay-Current-Wind-Speed'}
                Icon={(props) => (
                    <DynamicWindIndicator {...props} windSpeed={windSpeed ?? undefined} />
                )}
                name="Wind Speed"
                value={convertMPStoKMPH(windSpeed)}
                unit="km/h"
                emptyStateMessage="-"
                decimalPlaces={1}
            />
            <StatWithDirection
                id={'StatDisplay-Current-Wind-Direction'}
                directionOnly
                direction={windDirection ?? null}
                name="Wind Direction"
                emptyStateMessage="-"
            />
            <StatDisplay
                id={'StatDisplay-Current-UV-Index'}
                Icon={(props) => <DynamicUvIndicator {...props} uvIndex={uvIndex ?? undefined} />}
                name="UV Index"
                value={uvIndex}
                emptyStateMessage="-"
                decimalPlaces={1}
            />
        </>
    );
}
